(() => {
  const ROOT_SELECTOR = "[data-gum-filters]";

  const getRoot = (el) => (el instanceof Element ? el.closest(ROOT_SELECTOR) : null);
  const getSection = (root) => root.closest(".shopify-section");

  /* ------------------------------------------------------------------ *
   * Drawer
   * ------------------------------------------------------------------ */

  const setDrawer = (root, open) => {
    const drawer = root.querySelector("[data-gum-drawer]");

    if (!drawer) return;

    drawer.setAttribute("aria-hidden", open ? "false" : "true");
    document.documentElement.style.overflow = open ? "hidden" : "";

    // A closed drawer animates again on its next genuine open.
    if (!open) drawer.removeAttribute("data-gum-no-anim");

    root.querySelectorAll("[data-gum-open-drawer]").forEach((button) => {
      button.setAttribute("aria-expanded", open ? "true" : "false");
    });
  };

  const isDrawerOpen = (root) => {
    const drawer = root.querySelector("[data-gum-drawer]");
    return drawer ? drawer.getAttribute("aria-hidden") === "false" : false;
  };

  /* ------------------------------------------------------------------ *
   * Sort dropdown
   * ------------------------------------------------------------------ */

  const setSortOpen = (sort, open) => {
    const toggle = sort.querySelector("[data-gum-sort-toggle]");
    const list = sort.querySelector(".gum-filters__sort-list");

    if (!toggle || !list) return;

    list.hidden = !open;
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
  };

  // Rewrite each sort option's href so the current filters survive the sort.
  const rewriteSortHrefs = (root) => {
    root.querySelectorAll("[data-gum-sort-option]").forEach((option) => {
      const params = new URLSearchParams(window.location.search);
      params.set("sort_by", option.dataset.value);
      params.delete("page");
      option.href = `${window.location.pathname}?${params.toString()}`;
    });
  };

  /* ------------------------------------------------------------------ *
   * AJAX apply: fetch the section render for a URL and swap it in, so
   * filtering and sorting never reload the page. Falls back to a normal
   * navigation if anything goes wrong.
   * ------------------------------------------------------------------ */

  const formToUrl = (form) => {
    const params = new URLSearchParams(new FormData(form));
    const action = form.getAttribute("action") || window.location.pathname;
    return `${action}?${params.toString()}`;
  };

  // Inline scripts inserted via innerHTML never execute, so re-create them
  // (the price slider script guards itself against double-binding).
  const runInlineScripts = (container) => {
    container.querySelectorAll("script:not([src])").forEach((old) => {
      const script = document.createElement("script");
      script.textContent = old.textContent;
      old.replaceWith(script);
    });
  };

  const applyUrl = (root, url, { keepDrawer = false, pushHistory = true } = {}) => {
    const section = getSection(root);
    const target = new URL(url, window.location.origin);

    if (!section || !section.id.startsWith("shopify-section-")) {
      window.location.assign(target);
      return;
    }

    const sectionId = section.id.replace("shopify-section-", "");
    const fetchUrl = new URL(target);
    fetchUrl.searchParams.set("section_id", sectionId);

    // State to restore after the swap.
    const drawerWasOpen = keepDrawer && isDrawerOpen(root);
    const panel = section.querySelector(".gum-filters__drawer-panel");
    const panelScroll = panel ? panel.scrollTop : 0;
    const openGroups = [...section.querySelectorAll(".gum-filter-group[open]")]
      .map((group) => group.querySelector(".gum-filter-group__summary span")?.textContent.trim())
      .filter(Boolean);
    const active = document.activeElement;
    const focusKey =
      active && section.contains(active) && active.name
        ? { name: active.name, value: active.value }
        : null;

    section.setAttribute("aria-busy", "true");

    fetch(fetchUrl)
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const fresh = doc.getElementById(section.id);

        if (!fresh) throw new Error("section missing from response");

        section.innerHTML = fresh.innerHTML;
        runInlineScripts(section);

        if (pushHistory) {
          history.pushState({ gumFilters: true }, "", target);
        }

        const newRoot = section.querySelector(ROOT_SELECTOR);

        if (newRoot) {
          newRoot.querySelectorAll(".gum-filter-group").forEach((group) => {
            const label = group.querySelector(".gum-filter-group__summary span")?.textContent.trim();
            if (label && openGroups.includes(label)) {
              group.setAttribute("open", "");
            }
          });

          if (drawerWasOpen) {
            // The drawer was already open before the swap, so restoring it
            // must not replay the slide-in animation.
            const newDrawer = newRoot.querySelector("[data-gum-drawer]");
            if (newDrawer) newDrawer.setAttribute("data-gum-no-anim", "");

            setDrawer(newRoot, true);
            const newPanel = newRoot.querySelector(".gum-filters__drawer-panel");
            if (newPanel) newPanel.scrollTop = panelScroll;

            if (focusKey) {
              const refocus = newRoot.querySelector(
                `input[name="${CSS.escape(focusKey.name)}"][value="${CSS.escape(focusKey.value)}"]`
              );
              if (refocus) refocus.focus({ preventScroll: true });
            }
          } else {
            document.documentElement.style.overflow = "";
          }

          rewriteSortHrefs(newRoot);
        }
      })
      .catch(() => {
        window.location.assign(target);
      })
      .finally(() => {
        section.removeAttribute("aria-busy");
      });
  };

  /* ------------------------------------------------------------------ *
   * Delegated events: the whole section is replaced on every AJAX apply,
   * so nothing may be bound to elements inside it.
   * ------------------------------------------------------------------ */

  document.addEventListener("click", (event) => {
    const root = getRoot(event.target);

    // Close any sort dropdown the click landed outside of.
    document.querySelectorAll("[data-gum-sort]").forEach((sort) => {
      if (!sort.contains(event.target)) setSortOpen(sort, false);
    });

    if (!root) return;

    if (event.target.closest("[data-gum-open-drawer]")) {
      setDrawer(root, true);
      return;
    }

    if (event.target.closest("[data-gum-close-drawer]")) {
      setDrawer(root, false);
      return;
    }

    const sortToggle = event.target.closest("[data-gum-sort-toggle]");

    if (sortToggle) {
      const sort = sortToggle.closest("[data-gum-sort]");
      const list = sort.querySelector(".gum-filters__sort-list");
      setSortOpen(sort, list.hidden);
      return;
    }

    const sortOption = event.target.closest("[data-gum-sort-option]");

    if (sortOption) {
      event.preventDefault();
      setSortOpen(sortOption.closest("[data-gum-sort]"), false);
      applyUrl(root, sortOption.href);
      return;
    }

    const clear = event.target.closest(".gum-filter-form__clear");

    if (clear) {
      event.preventDefault();
      applyUrl(root, clear.href, { keepDrawer: true });
    }
  });

  document.addEventListener("change", (event) => {
    const input = event.target.closest("[data-gum-auto-submit]");

    if (!input) return;

    const root = getRoot(input);
    const form = input.closest("form");

    if (root && form) {
      applyUrl(root, formToUrl(form), { keepDrawer: true });
    }
  });

  // Price slider (and Enter in its inputs) submits the form.
  document.addEventListener("submit", (event) => {
    const form = event.target.closest(".gum-filter-form");
    const root = form ? getRoot(form) : null;

    if (root) {
      event.preventDefault();
      applyUrl(root, formToUrl(form), { keepDrawer: true });
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;

    document.querySelectorAll("[data-gum-sort]").forEach((sort) => setSortOpen(sort, false));
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => setDrawer(root, false));
  });

  // Back/forward re-renders the section for the restored URL.
  window.addEventListener("popstate", () => {
    const root = document.querySelector(ROOT_SELECTOR);

    if (root) {
      applyUrl(root, window.location.href, { pushHistory: false });
    }
  });

  /* ------------------------------------------------------------------ *
   * Initial load
   * ------------------------------------------------------------------ */

  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    rewriteSortHrefs(root);

    // On unfiltered page loads the facet counts ignore stock (they only
    // reflect the filters in the URL). Fetch the availability-filtered
    // render of this section and swap in its filter forms so counts and
    // disabled options are stock-aware from the start.
    const params = new URLSearchParams(window.location.search);
    let hasFilterParam = false;

    params.forEach((value, key) => {
      if (key.startsWith("filter.")) {
        hasFilterParam = true;
      }
    });

    const section = getSection(root);

    if (!hasFilterParam && section) {
      const url = `${window.location.pathname}?filter.v.availability=1&section_id=${encodeURIComponent(
        section.id.replace("shopify-section-", "")
      )}`;

      fetch(url)
        .then((response) => (response.ok ? response.text() : Promise.reject()))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");

          root.querySelectorAll(".gum-filter-form").forEach((form) => {
            const fresh = doc.getElementById(form.id);

            if (fresh) {
              form.innerHTML = fresh.innerHTML;
              runInlineScripts(form);
            }
          });
        })
        .catch(() => {});
    }
  });
})();
